// src/Studio/engine/motionPathEngine.ts
// Motion Path Engine (Phase 5) - Computes element position along custom paths
// Supports bezier and polyline paths with O(1) lookup via precomputed segment lengths

import type { MotionPath, MotionPathPoint, MotionPathType } from "../types";

// ==================== Types ====================

export interface PathPosition {
  x: number;
  y: number;
  tangentAngle: number; // Radians, for autoRotate
}

export interface PrecomputedPath {
  type: MotionPathType;
  points: MotionPathPoint[];
  totalLength: number;
  segmentLengths: number[];
  cumulativeLengths: number[]; // length at start of each segment (0..n)
  segmentIndexLut: number[]; // O(1) approx mapping from distance ratio -> segment index
  closed: boolean;
}

// ==================== Bezier Utilities ====================

function cubicBezierPoint(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  p3: { x: number; y: number },
  t: number
): { x: number; y: number } {
  const t2 = t * t;
  const t3 = t2 * t;
  const mt = 1 - t;
  const mt2 = mt * mt;
  const mt3 = mt2 * mt;

  return {
    x: mt3 * p0.x + 3 * mt2 * t * p1.x + 3 * mt * t2 * p2.x + t3 * p3.x,
    y: mt3 * p0.y + 3 * mt2 * t * p1.y + 3 * mt * t2 * p2.y + t3 * p3.y,
  };
}

function cubicBezierDerivative(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  p3: { x: number; y: number },
  t: number
): { x: number; y: number } {
  const t2 = t * t;
  const mt = 1 - t;
  const mt2 = mt * mt;

  return {
    x: 3 * mt2 * (p1.x - p0.x) + 6 * mt * t * (p2.x - p1.x) + 3 * t2 * (p3.x - p2.x),
    y: 3 * mt2 * (p1.y - p0.y) + 6 * mt * t * (p2.y - p1.y) + 3 * t2 * (p3.y - p2.y),
  };
}

// Approximate bezier segment length using subdivision
function bezierSegmentLength(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  p3: { x: number; y: number },
  samples: number = 20
): number {
  let length = 0;
  let prev = p0;

  for (let i = 1; i <= samples; i++) {
    const t = i / samples;
    const curr = cubicBezierPoint(p0, p1, p2, p3, t);
    length += Math.sqrt((curr.x - prev.x) ** 2 + (curr.y - prev.y) ** 2);
    prev = curr;
  }

  return length;
}

// ==================== Polyline Utilities ====================

function lineSegmentLength(
  p0: { x: number; y: number },
  p1: { x: number; y: number }
): number {
  return Math.sqrt((p1.x - p0.x) ** 2 + (p1.y - p0.y) ** 2);
}

// ==================== Precomputation ====================

/**
 * Precompute path data for O(1) lookup during animation.
 * Call this once when path is created/modified.
 */
export function precomputePath(path: MotionPath): PrecomputedPath {
  const { type, points, closed = false } = path;
  const segmentLengths: number[] = [];
  const cumulativeLengths: number[] = [0];
  let totalLength = 0;

  if (points.length < 2) {
    return { type, points, totalLength: 0, segmentLengths: [], cumulativeLengths: [0], segmentIndexLut: [], closed };
  }

  const numSegments = closed ? points.length : points.length - 1;

  for (let i = 0; i < numSegments; i++) {
    const p0 = points[i];
    const p1 = points[(i + 1) % points.length];
    let segmentLength: number;

    if (type === "bezier") {
      // Use control points for bezier curve
      const cp0 = p0.cpOut || p0;
      const cp1 = p1.cpIn || p1;
      segmentLength = bezierSegmentLength(p0, cp0, cp1, p1);
    } else {
      // Simple line segment
      segmentLength = lineSegmentLength(p0, p1);
    }

    segmentLengths.push(segmentLength);
    totalLength += segmentLength;
    cumulativeLengths.push(totalLength);
  }

  // Build an O(1) lookup table from normalized distance -> segment index.
  // This avoids a per-frame scan/binary search during playback.
  const LUT_SIZE = 256;
  const segmentIndexLut: number[] = new Array(LUT_SIZE).fill(0);
  if (totalLength > 0 && segmentLengths.length > 0) {
    let seg = 0;
    for (let i = 0; i < LUT_SIZE; i++) {
      const target = (i / (LUT_SIZE - 1)) * totalLength;
      while (seg < segmentLengths.length - 1 && cumulativeLengths[seg + 1] < target) {
        seg++;
      }
      segmentIndexLut[i] = seg;
    }
  }

  return { type, points, totalLength, segmentLengths, cumulativeLengths, segmentIndexLut, closed };
}

// ==================== Path Evaluation ====================

/**
 * Get position and tangent angle at progress t (0-1) along the path.
 * O(log n) lookup via binary search on segment lengths.
 */
export function evaluatePath(
  precomputed: PrecomputedPath,
  progress: number
): PathPosition {
  const { type, points, totalLength, segmentLengths, cumulativeLengths, segmentIndexLut, closed } = precomputed;

  // Handle edge cases
  if (points.length === 0) {
    return { x: 0, y: 0, tangentAngle: 0 };
  }

  if (points.length === 1) {
    return { x: points[0].x, y: points[0].y, tangentAngle: 0 };
  }

  // Clamp progress to [0, 1]
  const t = Math.max(0, Math.min(1, progress));

  // If at the very end
  if (t >= 1) {
    const lastIdx = closed ? 0 : points.length - 1;
    const prevIdx = closed ? points.length - 1 : points.length - 2;
    const p0 = points[prevIdx];
    const p1 = points[lastIdx];
    const tangentAngle = Math.atan2(p1.y - p0.y, p1.x - p0.x);
    return { x: p1.x, y: p1.y, tangentAngle };
  }

  // Find target distance along path
  const targetDist = t * totalLength;

  // O(1) segment lookup via LUT (with a tiny correction loop for edge cases).
  let segmentIndex = 0;
  if (segmentIndexLut.length > 0 && totalLength > 0) {
    const idx = Math.min(segmentIndexLut.length - 1, Math.max(0, Math.floor((targetDist / totalLength) * (segmentIndexLut.length - 1))));
    segmentIndex = segmentIndexLut[idx] ?? 0;
    // Correct forward if needed.
    while (segmentIndex < segmentLengths.length - 1 && cumulativeLengths[segmentIndex + 1] < targetDist) {
      segmentIndex++;
    }
    // Correct backward if needed.
    while (segmentIndex > 0 && cumulativeLengths[segmentIndex] > targetDist) {
      segmentIndex--;
    }
  }

  // Calculate local t within this segment
  const segmentLength = segmentLengths[segmentIndex];
  const distInSegment = targetDist - (cumulativeLengths[segmentIndex] ?? 0);
  const localT = segmentLength > 0 ? distInSegment / segmentLength : 0;

  // Get segment endpoints
  const p0 = points[segmentIndex];
  const p1 = points[(segmentIndex + 1) % points.length];

  let x: number, y: number, tangentAngle: number;

  if (type === "bezier") {
    // Cubic bezier interpolation
    const cp0 = p0.cpOut || p0;
    const cp1 = p1.cpIn || p1;

    const pos = cubicBezierPoint(p0, cp0, cp1, p1, localT);
    const deriv = cubicBezierDerivative(p0, cp0, cp1, p1, localT);

    x = pos.x;
    y = pos.y;
    tangentAngle = Math.atan2(deriv.y, deriv.x);
  } else {
    // Linear interpolation for polyline
    x = p0.x + (p1.x - p0.x) * localT;
    y = p0.y + (p1.y - p0.y) * localT;
    tangentAngle = Math.atan2(p1.y - p0.y, p1.x - p0.x);
  }

  return { x, y, tangentAngle };
}

// ==================== Path Presets ====================

/**
 * Generate preset path around element center.
 * All coordinates are relative to element's base position.
 */
export type PathPresetType =
  | "line" | "arc" | "s-curve" | "loop" | "zigzag"
  | "spiral" | "wave" | "bounce" | "figure8" | "heart" | "star" | "orbit";

export function generatePathPreset(
  preset: PathPresetType,
  elementBounds: { x: number; y: number; width: number; height: number }
): MotionPath {
  const { x, y, width, height } = elementBounds;
  const cx = x + width / 2;
  const cy = y + height / 2;
  const rx = width * 0.8; // Horizontal radius
  const ry = height * 0.6; // Vertical radius

  switch (preset) {
    case "line": {
      // Straight line across element center
      const points: MotionPathPoint[] = [
        { x: cx - rx, y: cy },
        { x: cx + rx, y: cy },
      ];
      return { type: "polyline", points, autoRotate: false };
    }

    case "arc": {
      // Simple arc from left to right above element
      const points: MotionPathPoint[] = [
        { x: cx - rx, y: cy, cpOut: { x: cx - rx, y: cy - ry } },
        { x: cx, y: cy - ry, cpIn: { x: cx - rx / 2, y: cy - ry }, cpOut: { x: cx + rx / 2, y: cy - ry } },
        { x: cx + rx, y: cy, cpIn: { x: cx + rx, y: cy - ry } },
      ];
      return { type: "bezier", points, autoRotate: false };
    }

    case "s-curve": {
      // S-shaped curve
      const points: MotionPathPoint[] = [
        { x: cx - rx, y: cy + ry / 2, cpOut: { x: cx - rx / 2, y: cy + ry / 2 } },
        { x: cx, y: cy, cpIn: { x: cx - rx / 3, y: cy }, cpOut: { x: cx + rx / 3, y: cy } },
        { x: cx + rx, y: cy - ry / 2, cpIn: { x: cx + rx / 2, y: cy - ry / 2 } },
      ];
      return { type: "bezier", points, autoRotate: false };
    }

    case "loop": {
      // Circular loop
      const k = 0.5522847498; // Magic number for circle approximation
      const points: MotionPathPoint[] = [
        { x: cx, y: cy - ry, cpIn: { x: cx - rx * k, y: cy - ry }, cpOut: { x: cx + rx * k, y: cy - ry } },
        { x: cx + rx, y: cy, cpIn: { x: cx + rx, y: cy - ry * k }, cpOut: { x: cx + rx, y: cy + ry * k } },
        { x: cx, y: cy + ry, cpIn: { x: cx + rx * k, y: cy + ry }, cpOut: { x: cx - rx * k, y: cy + ry } },
        { x: cx - rx, y: cy, cpIn: { x: cx - rx, y: cy + ry * k }, cpOut: { x: cx - rx, y: cy - ry * k } },
      ];
      return { type: "bezier", points, autoRotate: false, closed: true };
    }

    case "zigzag": {
      // Zigzag path
      const points: MotionPathPoint[] = [
        { x: cx - rx, y: cy },
        { x: cx - rx / 2, y: cy - ry / 2 },
        { x: cx, y: cy },
        { x: cx + rx / 2, y: cy + ry / 2 },
        { x: cx + rx, y: cy },
      ];
      return { type: "polyline", points, autoRotate: false };
    }

    case "spiral": {
      // Outward spiral (polyline with 60 points)
      const points: MotionPathPoint[] = [];
      const turns = 3; // Number of spiral turns
      const samples = 60;
      for (let i = 0; i <= samples; i++) {
        const t = i / samples;
        const angle = t * turns * Math.PI * 2;
        const r = t; // Radius grows linearly from 0 to 1
        points.push({
          x: cx + Math.cos(angle) * rx * r,
          y: cy + Math.sin(angle) * ry * r,
        });
      }
      return { type: "polyline", points, autoRotate: false };
    }

    case "wave": {
      // Sine wave pattern (polyline)
      const points: MotionPathPoint[] = [];
      const waves = 2; // Number of wave cycles
      const samples = 40;
      for (let i = 0; i <= samples; i++) {
        const t = i / samples;
        const xPos = cx - rx + t * rx * 2;
        const yPos = cy + Math.sin(t * waves * Math.PI * 2) * ry * 0.5;
        points.push({ x: xPos, y: yPos });
      }
      return { type: "polyline", points, autoRotate: false };
    }

    case "bounce": {
      // Parabolic bounce trajectory (polyline)
      const points: MotionPathPoint[] = [];
      const bounces = 3;
      const samples = 50;
      for (let i = 0; i <= samples; i++) {
        const t = i / samples;
        const xPos = cx - rx + t * rx * 2;
        // Create bouncing parabolas
        const bouncePhase = (t * bounces) % 1;
        const yPos = cy + ry * 0.5 - Math.abs(Math.sin(bouncePhase * Math.PI)) * ry;
        points.push({ x: xPos, y: yPos });
      }
      return { type: "polyline", points, autoRotate: false };
    }

    case "figure8": {
      // Lemniscate/infinity loop (bezier)
      const k = 0.5522847498;
      const halfRx = rx * 0.5;
      const points: MotionPathPoint[] = [
        // Center crossing point
        { x: cx, y: cy, cpIn: { x: cx - halfRx * k, y: cy - ry * k }, cpOut: { x: cx + halfRx * k, y: cy - ry * k } },
        // Right loop top
        { x: cx + halfRx, y: cy - ry * 0.5, cpIn: { x: cx + halfRx, y: cy - ry * 0.5 + ry * 0.3 }, cpOut: { x: cx + halfRx, y: cy - ry * 0.5 - ry * 0.3 } },
        // Right side
        { x: cx + rx, y: cy, cpIn: { x: cx + rx, y: cy - ry * k * 0.5 }, cpOut: { x: cx + rx, y: cy + ry * k * 0.5 } },
        // Right loop bottom
        { x: cx + halfRx, y: cy + ry * 0.5, cpIn: { x: cx + halfRx, y: cy + ry * 0.5 - ry * 0.3 }, cpOut: { x: cx + halfRx, y: cy + ry * 0.5 + ry * 0.3 } },
        // Center crossing
        { x: cx, y: cy, cpIn: { x: cx + halfRx * k, y: cy + ry * k }, cpOut: { x: cx - halfRx * k, y: cy + ry * k } },
        // Left loop bottom
        { x: cx - halfRx, y: cy + ry * 0.5, cpIn: { x: cx - halfRx, y: cy + ry * 0.5 - ry * 0.3 }, cpOut: { x: cx - halfRx, y: cy + ry * 0.5 + ry * 0.3 } },
        // Left side
        { x: cx - rx, y: cy, cpIn: { x: cx - rx, y: cy + ry * k * 0.5 }, cpOut: { x: cx - rx, y: cy - ry * k * 0.5 } },
        // Left loop top
        { x: cx - halfRx, y: cy - ry * 0.5, cpIn: { x: cx - halfRx, y: cy - ry * 0.5 + ry * 0.3 }, cpOut: { x: cx - halfRx, y: cy - ry * 0.5 - ry * 0.3 } },
      ];
      return { type: "bezier", points, autoRotate: false, closed: true };
    }

    case "heart": {
      // Heart shape (bezier)
      const points: MotionPathPoint[] = [
        // Bottom point
        { x: cx, y: cy + ry, cpIn: { x: cx + rx * 0.3, y: cy + ry * 0.6 }, cpOut: { x: cx - rx * 0.3, y: cy + ry * 0.6 } },
        // Left curve
        { x: cx - rx * 0.5, y: cy, cpIn: { x: cx - rx * 0.5, y: cy + ry * 0.4 }, cpOut: { x: cx - rx * 0.5, y: cy - ry * 0.3 } },
        // Left top bump
        { x: cx - rx * 0.25, y: cy - ry * 0.5, cpIn: { x: cx - rx * 0.6, y: cy - ry * 0.5 }, cpOut: { x: cx - rx * 0.1, y: cy - ry * 0.5 } },
        // Top center dip
        { x: cx, y: cy - ry * 0.3, cpIn: { x: cx - rx * 0.1, y: cy - ry * 0.4 }, cpOut: { x: cx + rx * 0.1, y: cy - ry * 0.4 } },
        // Right top bump
        { x: cx + rx * 0.25, y: cy - ry * 0.5, cpIn: { x: cx + rx * 0.1, y: cy - ry * 0.5 }, cpOut: { x: cx + rx * 0.6, y: cy - ry * 0.5 } },
        // Right curve
        { x: cx + rx * 0.5, y: cy, cpIn: { x: cx + rx * 0.5, y: cy - ry * 0.3 }, cpOut: { x: cx + rx * 0.5, y: cy + ry * 0.4 } },
      ];
      return { type: "bezier", points, autoRotate: false, closed: true };
    }

    case "star": {
      // 5-point star path (polyline)
      const points: MotionPathPoint[] = [];
      const outerR = 1;
      const innerR = 0.4;
      for (let i = 0; i < 10; i++) {
        const angle = (i * Math.PI / 5) - Math.PI / 2; // Start from top
        const r = i % 2 === 0 ? outerR : innerR;
        points.push({
          x: cx + Math.cos(angle) * rx * r,
          y: cy + Math.sin(angle) * ry * r,
        });
      }
      return { type: "polyline", points, autoRotate: false, closed: true };
    }

    case "orbit": {
      // Elliptical orbit (bezier) - similar to loop but with different aspect ratio
      const k = 0.5522847498;
      const orbitRx = rx * 1.2; // Wider horizontal
      const orbitRy = ry * 0.4; // Flatter vertical for 3D perspective effect
      const points: MotionPathPoint[] = [
        { x: cx, y: cy - orbitRy, cpIn: { x: cx - orbitRx * k, y: cy - orbitRy }, cpOut: { x: cx + orbitRx * k, y: cy - orbitRy } },
        { x: cx + orbitRx, y: cy, cpIn: { x: cx + orbitRx, y: cy - orbitRy * k }, cpOut: { x: cx + orbitRx, y: cy + orbitRy * k } },
        { x: cx, y: cy + orbitRy, cpIn: { x: cx + orbitRx * k, y: cy + orbitRy }, cpOut: { x: cx - orbitRx * k, y: cy + orbitRy } },
        { x: cx - orbitRx, y: cy, cpIn: { x: cx - orbitRx, y: cy + orbitRy * k }, cpOut: { x: cx - orbitRx, y: cy - orbitRy * k } },
      ];
      return { type: "bezier", points, autoRotate: false, closed: true };
    }

    default:
      return { type: "polyline", points: [{ x, y }] };
  }
}

// ==================== Path Utilities ====================

/**
 * Offset all path points by a delta (for moving path with element).
 */
export function offsetPath(path: MotionPath, dx: number, dy: number): MotionPath {
  const offsetPoint = (p: MotionPathPoint): MotionPathPoint => ({
    x: p.x + dx,
    y: p.y + dy,
    cpIn: p.cpIn ? { x: p.cpIn.x + dx, y: p.cpIn.y + dy } : undefined,
    cpOut: p.cpOut ? { x: p.cpOut.x + dx, y: p.cpOut.y + dy } : undefined,
  });

  return {
    ...path,
    points: path.points.map(offsetPoint),
    // Invalidate precomputed data
    totalLength: undefined,
    segmentLengths: undefined,
  };
}

/**
 * Scale path around a center point.
 */
export function scalePath(
  path: MotionPath,
  scaleX: number,
  scaleY: number,
  centerX: number,
  centerY: number
): MotionPath {
  const scalePoint = (p: MotionPathPoint): MotionPathPoint => ({
    x: centerX + (p.x - centerX) * scaleX,
    y: centerY + (p.y - centerY) * scaleY,
    cpIn: p.cpIn
      ? { x: centerX + (p.cpIn.x - centerX) * scaleX, y: centerY + (p.cpIn.y - centerY) * scaleY }
      : undefined,
    cpOut: p.cpOut
      ? { x: centerX + (p.cpOut.x - centerX) * scaleX, y: centerY + (p.cpOut.y - centerY) * scaleY }
      : undefined,
  });

  return {
    ...path,
    points: path.points.map(scalePoint),
    totalLength: undefined,
    segmentLengths: undefined,
  };
}

/**
 * Get bounding box of path.
 */
export function getPathBounds(path: MotionPath): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  if (path.points.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  }

  let minX = Infinity, minY = Infinity;
  let maxX = -Infinity, maxY = -Infinity;

  for (const p of path.points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);

    // Include control points in bounds
    if (p.cpIn) {
      minX = Math.min(minX, p.cpIn.x);
      minY = Math.min(minY, p.cpIn.y);
      maxX = Math.max(maxX, p.cpIn.x);
      maxY = Math.max(maxY, p.cpIn.y);
    }
    if (p.cpOut) {
      minX = Math.min(minX, p.cpOut.x);
      minY = Math.min(minY, p.cpOut.y);
      maxX = Math.max(maxX, p.cpOut.x);
      maxY = Math.max(maxY, p.cpOut.y);
    }
  }

  return { minX, minY, maxX, maxY };
}

/**
 * Sample path to generate a polyline for rendering.
 */
export function samplePathForRendering(
  precomputed: PrecomputedPath,
  samples: number = 50
): { x: number; y: number }[] {
  const result: { x: number; y: number }[] = [];

  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const pos = evaluatePath(precomputed, t);
    result.push({ x: pos.x, y: pos.y });
  }

  return result;
}
